<template>
  <p>
    <i18n-t
      v-if="proposedDistances.length"
      keypath="increase radius or remove location"
    >
      <template #radius>
        <template v-for="distance in proposedDistances" :key="distance">
          <pu-low-results-search-link
            class="losenge"
            :modify-filters="{ within: distance }"
            :custom-query-params="{
              placename: queryPlacename,
              within: distance,
            }"
          >
            <translate :tt="{ value: distance }">
              {value, localeDistance, short}
            </translate>
          </pu-low-results-search-link>
        </template>
      </template>
      <template #yourLocation>
        <translate v-if="queryPlacename" tag="strong">
          {{ queryPlacename }}
        </translate>
        <translate v-else tag="strong"> your location </translate>
      </template>
      <template #location>
        <span class="losenge" @click="redirect">
          <translate v-if="queryPlacename">{{ queryPlacename }}</translate>
          <translate v-else>your location</translate>
        </span>
      </template>
    </i18n-t>
    <i18n-t v-else keypath="Try removing {location}.">
      <template #location>
        <span class="losenge" @click="redirect">
          <translate v-if="queryPlacename">{{ queryPlacename }}</translate>
          <translate v-else>your location</translate>
        </span>
      </template>
    </i18n-t>
  </p>
</template>

<script lang="ts">
import type { Pinia as _Pinia } from 'pinia'
import { ensureArray } from '~/utility/array'
import {
  getCurrentRangeValue,
  getProposedDistances,
} from '~/utility/geo/distance-units'
import type { GeoCircle } from '~/utility/geo/GeoLocation'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { routeToFilters } from '../../../pu-links/infrastructure/routeToFilters'
import { SearchLinkGenerator } from '../../../url/generators/SearchLinkGenerator'

@Component({
  setup() {
    const searchStore = useSearchStoreSetup(usePinia())
    return { searchStore }
  },
})
class LowResultsPoint extends Vue {
  @Prop({ required: true })
  area: GeoCircle

  step = 5
  maxDistanceFromActualWithin = 40
  topDistanceLimit = 95

  async redirect() {
    const filters = routeToFilters(this.$route)
    const urlData = new SearchLinkGenerator().getUrlData({
      excludeType: 'nearby',
      filters: { ...filters, area: undefined },
    })
    await this.$router.push({
      path: urlData.path,
      query: urlData.query as Record<string, any>,
    })
  }

  get queryPlacename() {
    return ensureArray(this.$route.query.placename)[0] || undefined
  }

  get isKilometers() {
    return this.$i18n.localeInfo.distanceUnit === 'km'
  }

  get proposedDistances(): number[] {
    /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- getCurrentRangeValue and $config.public are untyped */
    const currentRange = getCurrentRangeValue({
      searchStore: this.searchStore,
      isKilometers: this.isKilometers,
      area: this.area,
      defaultWithinRange: this.$config.public.defaultWithinRange,
    })
    /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */

    return getProposedDistances({
      currentRange,
      step: this.step,
      maxDistanceFromCurrent: this.maxDistanceFromActualWithin,
      topLimit: this.topDistanceLimit,
      isKilometers: this.isKilometers,
    })
  }
}

export default toNative(LowResultsPoint)
</script>
